import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import StatusHub from '@/services/status-hub';
import { HTTP, HTTPRoot } from '@/services/http-common';
import { auth } from '@/services/auth';

import moment from 'moment';
import BootstrapVue from 'bootstrap-vue';
import VueBootstrapTypeahead from 'vue-bootstrap-typeahead';
import Vuelidate from 'vuelidate';
import HighchartsVue from 'highcharts-vue';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import exportingInit from 'highcharts/modules/exporting';
import exportDataInit from 'highcharts/modules/export-data';
import offlineExportInit from 'highcharts/modules/offline-exporting';
import mapInit from 'highcharts/modules/map';
import VueScrollTo from 'vue-scrollto';

import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon, FontAwesomeLayers, FontAwesomeLayersText } from '@fortawesome/vue-fontawesome';

import PageLoading from '@/components/helpers/PageLoading';
import PageRunning from '@/components/helpers/PageRunning';
import ErrorList from '@/components/helpers/ErrorList';
import SaveButton from '@/components/helpers/SaveButton';
import BackButton from '@/components/helpers/BackButton';
import SuccessAlert from '@/components/helpers/SuccessAlert';
import FixedActionBar from '@/components/helpers/FixedActionBar';
import CheckIcon from '@/components/helpers/CheckIcon';
import PageAuthorizationContainer from '@/components/helpers/PageAuthorizationContainer';
import LoginForm from '@/components/LoginForm';

Vue.use(StatusHub);
Vue.prototype.$http = HTTP;
Vue.prototype.$httpRoot = HTTPRoot;

Vue.component('page-loading', PageLoading);
Vue.component('page-running', PageRunning);
Vue.component('error-list', ErrorList);
Vue.component('save-button', SaveButton);
Vue.component('back-button', BackButton);
Vue.component('success-alert', SuccessAlert);
Vue.component('fixed-action-bar', FixedActionBar);
Vue.component('check-icon', CheckIcon);
Vue.component('page-authorization-container', PageAuthorizationContainer);
Vue.component('login-form', LoginForm);

library.add(fas, far);
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('font-awesome-layers', FontAwesomeLayers);
Vue.component('font-awesome-layers-text', FontAwesomeLayersText);

Vue.config.productionTip = false;
Vue.use(BootstrapVue, {
	BButton: { variant: 'success' }
});
Vue.use(Vuelidate);

Vue.use(HighchartsVue);
Highcharts.setOptions({
	chart: {
		style: {
			fontFamily: '"Roboto", Arial, Helvetica, sans-serif'
		}
	},
	lang: {
		thousandsSep: '\u002c'
	}
});
HighchartsMore(Highcharts);
exportingInit(Highcharts);
exportDataInit(Highcharts);
offlineExportInit(Highcharts);
mapInit(Highcharts);

(function (H) {
	H.addEvent(H.Axis, 'init', function (e) {
		this.allowNegativeLog = e.userOptions.allowNegativeLog;
	});

	// Override conversions
	H.wrap(H.Axis.prototype, 'log2lin', function (proceed, num) {

		if (!this.allowNegativeLog) {
			return proceed.call(this, num);
		}

		var isNegative = num < 0,
			adjustedNum = Math.abs(num),
			result;
		if (adjustedNum < 10) {
			adjustedNum += (10 - adjustedNum) / 10;
		}
		result = Math.log(adjustedNum) / Math.LN10;
		return isNegative ? -result : result;
	});
	H.wrap(H.Axis.prototype, 'lin2log', function (proceed, num) {
		if (!this.allowNegativeLog) {
			return proceed.call(this, num);
		}

		var isNegative = num < 0,
			absNum = Math.abs(num),
			result = Math.pow(10, absNum);
		if (result < 10) {
			result = (10 * (result - 1)) / (10 - 1);
		}
		return isNegative ? -result : result;
	});
}(Highcharts));

Vue.component('b-typeahead', VueBootstrapTypeahead);

Vue.use(VueScrollTo, {
	container: "body",
	duration: 500,
	easing: "ease",
	offset: 0,
	force: true,
	cancelable: true,
	onStart: false,
	onDone: false,
	onCancel: false,
	x: false,
	y: true
});

Vue.filter('number', function (value, decimals, includeCommas = true) {
	if (isNaN(Number(value))) return value;
	var x = Number(value).toFixed(decimals);
	if (includeCommas) {
		var parts = x.toString().split(".");
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		return parts.join(".");
	}
	return x;
});

Vue.filter('date', function (value, format = 'llll') {
	if (value) {
		return moment(String(value)).format(format);
	}
});

Vue.filter('dateFromNow', function (value) {
	if (value) {
		return moment(String(value)).fromNow();
	}
});

Vue.filter('ifnull', function (value, nullvalue) {
	if (value == '' || value == null || value == undefined)
		return nullvalue;

	return value;
});

Vue.directive('autofocus', {
	inserted: function (el, binding) {
		if (binding.value || binding.value == undefined) {
			el.focus();
		}
	}
});

Vue.mixin(auth);
Vue.mixin({
	data() {
		return {
			globals: {
				mapColors: {
					default: {
						subbasin: '#e4fabe',
						downstream: '#b6dd6c',
						nearby: '#fff1d6',
						stream: '#006da0',
						selected: '#6fedda',
						highContrast: false
					},
					highContrast: {
						subbasin: '#FFB061',
						downstream: '#FF8000',
						nearby: '#fff1d6',
						stream: '#000FFF',
						selected: '#6fedda',
						highContrast: true
					}
				},
				passwordRequirements: 'Minimum 10 characters with at least one number, upper and lowercase letter, and special character'
			}
		}
	},
	methods: {
		isDevMode() {
			return process.env.NODE_ENV === 'development';
		},
		isNullOrEmpty(value) {
			return value === undefined || value == null || value == '';
		},
		capitalizeFirstLette(s) {
			return s.charAt(0).toUpperCase() + s.slice(1);
		},
		numberWithCommas(x) {
			var parts = x.toString().split(".");
			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
			return parts.join(".");
		},
		toDate(value, format = 'llll') {
			if (value) {
				return moment(String(value)).format(format);
			}
		},
		log(message) {
			if (this.isDevMode()) {
				console.log(message);
			}
		},
		logError(error, defaultMessage = undefined) {
			let messages = [];
			if (defaultMessage) {
				messages.push(defaultMessage);
			}

			if (error.response) {
				// The request was made and the server responded with a status code
				// that falls out of the range of 2xx
				console.log(error.response.data);
				//console.log(error.response.status);
				//console.log(error.response.headers);

				if (error.response.status === 401 || error.response.status === 403) {
					messages.push('Invalid credentials. Either your user name or password is incorrect, or you do not have permission to view this page.');
				} else if (error.response.status === 404) {
					messages.push('Requested data not found. Please check the URL in your browser and make sure you have the correct ID numbers if any.');
				} else if (error.response.data && error.response.data.errors) {
					let errors = error.response.data.errors;
					for (let key in errors) {
						for (let j = 0; j < errors[key].length; j++) {
							messages.push(errors[key][j]);
						}
					}
				} else if (error.response.data) {
					if (typeof error.response.data === 'string' || error.response.data instanceof String)
						messages.push(error.response.data);
					else {
						try {
							let errors = Object.keys(error.response.data);
							for (let key of errors) {
								for (let val of error.response.data[key])
									messages.push(val);
							}
						} catch (e) {
							this.log(e);
							messages.push('There was an error processing your request.');
						}
					}
				}

				if (error.response.data.stackTrace) {
					console.log(error.response.data.stackTrace);
				}
			} else if (error.request) {
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
				// http.ClientRequest in node.js
				console.log(error.request);
			} else {
				// Something happened in setting up the request that triggered an Error
				console.log('Error', error.message);
			}
			//console.log(error.config);

			return messages;
		},
		errorContainsKey(error, key) {
			return error.response && error.response.data && error.response.data.errors && Object.prototype.hasOwnProperty.call(error.response.data.errors, key);
		},
		setMapColors(highContrast = true) {
			var colors = highContrast ? this.globals.mapColors.highContrast : this.globals.mapColors.default;
			localStorage.setItem('map_colors', JSON.stringify(colors));
			this.$store.commit('setMapColors', colors);
		},
		loadMapColors() {
			var storedColors = JSON.parse(localStorage.getItem('map_colors'));
			if (!storedColors) {
				storedColors = this.globals.mapColors.default;
				localStorage.setItem('map_colors', JSON.stringify(storedColors));
			}
			this.$store.commit('setMapColors', storedColors);
			this.log(storedColors);
			this.log(this.mapColors);
		}
	},
	computed: {
		getValidState() {
			return (prop) => {
				return prop.$dirty ? !prop.$error : null;
			}
		},
		requiredFeedback() {
			return (prop) => {
				if (!prop.required) return 'Required';
			}
		}
	}
})

new Vue({
	router,
	store,
	render: h => h(App)
}).$mount('#app')
